// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useResizeObserver } from '@cloudscape-design/component-toolkit/internal';
import { getBaseProps } from '../internal/base-component';
import { TableContextProvider } from './context';
import { computeGridTemplateColumns } from './grid-template-columns';
import styles from './styles.css.js';
export default function InternalTableRoot({ columnLayout = { type: 'auto' }, ariaRowcount, ariaLabel, ariaLabelledby, ariaDescribedby, children, __internalRootRef, ...rest }) {
    const isGrid = columnLayout.type === 'grid';
    const gridTemplateColumns = computeGridTemplateColumns(columnLayout);
    // Memoized because it is the TableContext value; a fresh object re-renders every cell. Both fields are
    // primitives compared by value, so it stays stable even when the caller passes a fresh columnLayout.
    const tableContext = useMemo(() => ({ isGrid, gridTemplateColumns }), [isGrid, gridTemplateColumns]);
    const baseProps = getBaseProps(rest);
    // A wide table's horizontal scroller isn't keyboard-reachable on its own, so a read-only table with no
    // focusable cell content can't be scrolled by keyboard. When the content overflows, expose the scroller
    // as a focusable region so keyboard users can scroll it horizontally, matching the existing Table's
    // getTableWrapperRoleProps.
    const scrollerRef = useRef(null);
    const [isScrollable, setIsScrollable] = useState(false);
    const measureScrollable = useCallback(() => {
        const node = scrollerRef.current;
        if (node) {
            setIsScrollable(node.scrollWidth - node.clientWidth > 1);
        }
    }, []);
    // Observe the scroller and the table it wraps: a viewport resize changes the scroller box, and
    // auto-layout content growth changes the table box — either can start or stop the horizontal overflow.
    useResizeObserver(scrollerRef, measureScrollable);
    useResizeObserver(() => { var _a, _b; return (_b = (_a = scrollerRef.current) === null || _a === void 0 ? void 0 : _a.firstElementChild) !== null && _b !== void 0 ? _b : null; }, measureScrollable);
    // Grid column templates change the tracks' overflow without resizing either observed box, so no observer
    // fires — re-measure when the template changes.
    useEffect(() => {
        if (isGrid) {
            measureScrollable();
        }
    }, [isGrid, gridTemplateColumns, measureScrollable]);
    // Set role="region" whenever scrollable (label passes through even if undefined), matching the
    // existing Table's getTableWrapperRoleProps rather than gating the role on a label.
    const scrollRegionProps = isScrollable
        ? {
            role: 'region',
            tabIndex: 0,
            'aria-label': ariaLabel,
            'aria-labelledby': ariaLabelledby,
        }
        : {};
    return (React.createElement("div", { ...baseProps, className: clsx(baseProps.className, styles.root), ref: __internalRootRef },
        React.createElement(TableContextProvider, { value: tableContext },
            React.createElement("div", { className: styles['body-scroller'], ref: scrollerRef, ...scrollRegionProps },
                React.createElement("table", { 
                    // Grid mode only: display:grid drops the table's implicit role. Auto mode keeps the
                    // native role (an explicit role="table" there is redundant and flagged by a11y validators).
                    role: isGrid ? 'table' : undefined, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledby, "aria-describedby": ariaDescribedby, "aria-rowcount": ariaRowcount, className: clsx(styles.table, isGrid ? styles['table-grid'] : styles['table-auto']) }, children)))));
}
//# sourceMappingURL=internal.js.map